import React, { useCallback, useMemo } from 'react';
import clsx, { ClassValue } from 'clsx';
import { useSortable } from '@dnd-kit/react/sortable';
import { ExperimentMetric, ExperimentMetricResults, ExperimentMetricResultsFormat, ExperimentMetricType, StatisticallySignificant } from 'apps/experiments/src/types';
import { EMPTY_METRIC_ID, MetricResult, useExperimentsStore } from 'apps/experiments/src/store';
import { ButtonReact } from 'common/components/button';
import { useTranslation } from 'common/i18n/hooks';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { useExperimentPageContext } from 'apps/experiments/src/components/context';
import { getFinalDisplayOptions } from 'common/report/insights/charts/util';
import { eventPropertyDefinitionsStore } from 'common/report/stores/property-definitions';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { formatMetricNumber } from 'common/util';
import { TooltipReact } from 'common/components/tooltip';
import { Placement, TooltipTextAlign } from 'common/components/tooltip/types';
import { SvgIconReact } from 'common/components/svg-icon';
import { PageTrackingEvent, trackExperimentPageAction, TrackingEventSource } from 'apps/experiments/src/components/tracking';
import { useExperimentMetrics } from 'apps/experiments/src/components/hooks/metrics';
import { useOpenInNewTab } from 'common/contexts/react/open-in-new-tab-context';
import { convertExperimentMetricToInsightUrl } from 'apps/experiments/src/components/utils';
import { PropertyDisplayOptions } from 'apps/lexicon/src/models/property-definition';
import { useExperimentGlobalFilters } from 'apps/experiments/src/components/hooks/global-filters';
import { isSavedMetricOrFormula } from 'common/report/insights/models/utils';
import { useStagedExperiment } from 'apps/experiments/src/components/hooks/staged-experiment';
import { SpinnerSize } from 'common/components/spinner/types';
import { SpinnerReact } from 'common/components/spinner';
import { SORTABLE_METRIC_TYPE } from '../const';
import styles from './_row.module.styl';
import { ExperimentPageSummaryMetricsCategoryChart } from './_chart';
type ExperimentPageSummaryMetricsCategoryRowProps = {
  index: number;
  metric: ExperimentMetric;
  type: ExperimentMetricType;
  variantNames: string[];
  absMax: number;
  results?: MetricResult;
};
type ButtonClickHandler = (ev: {
  stopPropagation: () => void;
}) => void;
function DeleteMetricButton(_ref: Pick<ExperimentMetric, `id`>) {
  let {
    id
  }: Pick<ExperimentMetric, `id`> = _ref;
  const {
    deleteMetric
  } = useExperimentMetrics();
  const onClick: ButtonClickHandler = useCallback(ev => {
    ev.stopPropagation();
    deleteMetric(id);
  }, [id, deleteMetric]);
  return <ButtonReact className={clsx(styles.hoverOnly, styles.rowAction)} icon='trashcan' theme={ButtonTheme.Tertiary} size={ButtonSize.Inline} onClick={onClick} data-sentry-element="ButtonReact" data-sentry-component="DeleteMetricButton" data-sentry-source-file="_row.tsx" />;
}
function EditMetricButton(_ref2: {
  metric: ExperimentMetric;
}) {
  let {
    metric
  }: {
    metric: ExperimentMetric;
  } = _ref2;
  const {
    selectMetric
  } = useExperimentMetrics();
  const onClick: ButtonClickHandler = useCallback(ev => {
    ev.stopPropagation();
    selectMetric(metric, TrackingEventSource.ExperimentPageMetrics);
  }, [metric, selectMetric]);
  return <ButtonReact className={clsx(styles.hoverOnly, styles.rowAction)} icon='edit' theme={ButtonTheme.Tertiary} size={ButtonSize.Inline} onClick={onClick} data-sentry-element="ButtonReact" data-sentry-component="EditMetricButton" data-sentry-source-file="_row.tsx" />;
}
const PERCENT_FORMATTER = new Intl.NumberFormat("default", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});
function formatRawValue(_ref3: {
  value: number;
  format: ExperimentMetricResultsFormat;
  displayOptions: Partial<PropertyDisplayOptions>;
}) {
  var _displayOptions$prefi, _displayOptions$suffi;
  let {
    value,
    format,
    displayOptions
  }: {
    value: number;
    format: ExperimentMetricResultsFormat;
    displayOptions: Partial<PropertyDisplayOptions>;
  } = _ref3;
  if (format === "percent") {
    return PERCENT_FORMATTER.format(value);
  }
  if (Number.isNaN(value)) {
    return "\u2014";
  }
  return "".concat((_displayOptions$prefi = displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.prefix) !== null && _displayOptions$prefi !== void 0 ? _displayOptions$prefi : "").concat(formatMetricNumber(value)).concat((_displayOptions$suffi = displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.suffix) !== null && _displayOptions$suffi !== void 0 ? _displayOptions$suffi : "");
}
function formatLiftValue(_ref4: {
  lift: number;
}) {
  let {
    lift
  }: {
    lift: number;
  } = _ref4;
  if (lift === Number.POSITIVE_INFINITY) {
    return "+\u221E";
  }
  if (lift === Number.NEGATIVE_INFINITY) {
    return "-\u221E";
  }
  if (Number.isNaN(lift)) {
    return "\u2014";
  }
  if (lift > 0) {
    return "\u2191".concat(PERCENT_FORMATTER.format(Math.abs(lift)));
  } else if (lift < 0) {
    return "\u2193".concat(PERCENT_FORMATTER.format(Math.abs(lift)));
  }
  return PERCENT_FORMATTER.format(lift);
}
function LiftValue(_ref5: {
  lift: number;
  notSupported?: boolean;
  isLoading?: boolean;
}) {
  let {
    lift,
    notSupported,
    isLoading
  }: {
    lift: number;
    notSupported?: boolean;
    isLoading?: boolean;
  } = _ref5;
  const {
    t
  } = useTranslation();
  if (isLoading) {
    return <div className={styles.metricValue}>
        <SpinnerReact size={SpinnerSize.Small} />
      </div>;
  }
  if (notSupported) {
    return <div className={styles.metricValue}>
        <SvgIconReact icon='info' />
        <TooltipReact delayIn={250} textAlign={TooltipTextAlign.Start} maxWidth='200px' text={t("experiments.main.metrics.lift-unsupported")} />
      </div>;
  }
  return <div className={clsx(styles.metricValue, styles.liftValue, styles.numericValue)} data-sentry-component="LiftValue" data-sentry-source-file="_row.tsx">{formatLiftValue({
      lift
    })}</div>;
}
function ExperimentMetricValue(_ref6: {
  value: number;
  format: ExperimentMetricResultsFormat;
  displayOptions: Partial<PropertyDisplayOptions>;
  isLoading?: boolean;
}) {
  let {
    value,
    format,
    displayOptions,
    isLoading
  }: {
    value: number;
    format: ExperimentMetricResultsFormat;
    displayOptions: Partial<PropertyDisplayOptions>;
    isLoading?: boolean;
  } = _ref6;
  return <div className={clsx(styles.metricValue, styles.numericValue)} data-sentry-component="ExperimentMetricValue" data-sentry-source-file="_row.tsx">
      {isLoading ? "..." : formatRawValue({
      value,
      format,
      displayOptions
    })}
    </div>;
}

/** Each metric row has 1 or more variant rows inside it.
 *
 * This is just the values and the chart part
 */
function VariantRow(_ref7: {
  variantName: string;
  results?: Partial<ExperimentMetricResults>;
  isLoading?: boolean;
  metric?: ExperimentMetric;
  className?: ClassValue;
  displayOptions: Partial<PropertyDisplayOptions>;
  absMax: number;
  displayVariantName?: boolean;
}) {
  let {
    variantName,
    results: {
      control = NaN,
      controlFormat = "number",
      treatment = NaN,
      treatmentFormat = "number",
      lift = NaN,
      liftConfidence = NaN,
      pValue = NaN,
      statisticallySignificant = StatisticallySignificant.NO,
      notSupported = false
    } = {},
    isLoading = false,
    className,
    displayOptions,
    absMax,
    displayVariantName
  }: {
    variantName: string;
    results?: Partial<ExperimentMetricResults>;
    isLoading?: boolean;
    metric?: ExperimentMetric;
    className?: ClassValue;
    displayOptions: Partial<PropertyDisplayOptions>;
    absMax: number;
    displayVariantName?: boolean;
  } = _ref7;
  return <div className={clsx(styles.variant, className)} data-sentry-component="VariantRow" data-sentry-source-file="_row.tsx">
      {displayVariantName ? <div className={styles.variantName}>
          <div className={styles.metricNameText}>{variantName}</div>
        </div> : null}
      <LiftValue lift={lift} notSupported={notSupported} isLoading={isLoading} data-sentry-element="LiftValue" data-sentry-source-file="_row.tsx" />
      <ExperimentMetricValue value={control} format={controlFormat} displayOptions={displayOptions} isLoading={isLoading} data-sentry-element="ExperimentMetricValue" data-sentry-source-file="_row.tsx" />
      <ExperimentMetricValue value={treatment} format={treatmentFormat} displayOptions={displayOptions} isLoading={isLoading} data-sentry-element="ExperimentMetricValue" data-sentry-source-file="_row.tsx" />
      <div className={styles.metricGraph}>
        <ExperimentPageSummaryMetricsCategoryChart absMax={absMax} lift={lift} liftConfidence={liftConfidence} pValue={pValue} statisticallySignificant={statisticallySignificant} data-sentry-element="ExperimentPageSummaryMetricsCategoryChart" data-sentry-source-file="_row.tsx" />
      </div>
    </div>;
}
function VariantRowError(_ref8: {
  error: Error;
}) {
  let {
    error
  }: {
    error: Error;
  } = _ref8;
  return <div className={styles.liftError} data-sentry-component="VariantRowError" data-sentry-source-file="_row.tsx">
      <SvgIconReact icon='warning' data-sentry-element="SvgIconReact" data-sentry-source-file="_row.tsx" />
      <TooltipReact delayIn={250} placement={Placement.RightCenter} textAlign={TooltipTextAlign.Start} maxWidth='200px' text={error.message} data-sentry-element="TooltipReact" data-sentry-source-file="_row.tsx" />
    </div>;
}
function MetricIcon(_ref9: {
  metric: ExperimentMetric;
}) {
  let {
    metric
  }: {
    metric: ExperimentMetric;
  } = _ref9;
  if (metric !== null && metric !== void 0 && metric.definition && isSavedMetricOrFormula(metric.definition)) {
    return <SvgIconReact icon='data-metric-outlined' />;
  }
  return <SvgIconReact icon='data-metric-dashed' data-sentry-element="SvgIconReact" data-sentry-component="MetricIcon" data-sentry-source-file="_row.tsx" />;
}
function MetricName(_ref10: {
  metric: ExperimentMetric;
  isMultiVariant: boolean;
}) {
  var _metric$definition;
  let {
    metric,
    isMultiVariant
  }: {
    metric: ExperimentMetric;
    isMultiVariant: boolean;
  } = _ref10;
  const {
    t
  } = useTranslation();
  return <div className={clsx(styles.metricName, isMultiVariant ? styles.metricNameMultiVariant : styles.metricNameSingleVariant)} data-sentry-component="MetricName" data-sentry-source-file="_row.tsx">
      <MetricIcon metric={metric} data-sentry-element="MetricIcon" data-sentry-source-file="_row.tsx" />

      <div className={styles.metricNameText}>
        {metric.name || (metric === null || metric === void 0 || (_metric$definition = metric.definition) === null || _metric$definition === void 0 ? void 0 : _metric$definition.name) || t("query-builder.select-metric")}
      </div>
    </div>;
}
function ExploreButton(_ref11: {
  metric: ExperimentMetric;
}) {
  let {
    metric
  }: {
    metric: ExperimentMetric;
  } = _ref11;
  const openInNewTab = useOpenInNewTab();
  const {
    stagedExperiment
  } = useStagedExperiment();
  const {
    projectId,
    workspaceId
  } = useExperimentPageContext();
  const {
    additionalFilters
  } = useExperimentGlobalFilters();
  const onClick: ButtonClickHandler = useCallback(async ev => {
    ev.stopPropagation();
    const {
      definition,
      dataGroupId
    } = metric;
    const url = await convertExperimentMetricToInsightUrl({
      experimentMetric: metric,
      experiment: stagedExperiment,
      projectId,
      workspaceId,
      additionalFilters
    });
    if (url) {
      openInNewTab(url, "_blank");
      trackExperimentPageAction({
        event: PageTrackingEvent.ExploreMetric,
        experiment: stagedExperiment,
        props: {
          dataGroupId,
          definition
        }
      });
    }
  }, [metric, stagedExperiment, projectId, workspaceId, additionalFilters]);
  return <ButtonReact className={clsx(styles.hoverOnly, styles.rowAction)} icon='line' theme={ButtonTheme.Tertiary} size={ButtonSize.Inline} onClick={onClick} data-sentry-element="ButtonReact" data-sentry-component="ExploreButton" data-sentry-source-file="_row.tsx" />;
}
const GrabHandle = React.forwardRef<HTMLDivElement>((props, ref) => {
  return <div {...props} ref={ref} className={clsx(styles.grabIndicator, styles.hoverOnly)}>
      <SvgIconReact icon='gripper-xs' />
    </div>;
});
export function ExperimentPageSummaryMetricsCategoryRow(props: ExperimentPageSummaryMetricsCategoryRowProps) {
  const metrics = useMetrics();
  const currentMetric = useExperimentsStore(state => state.currentMetric);
  const isActive = (currentMetric === null || currentMetric === void 0 ? void 0 : currentMetric.id) === props.metric.id;
  const {
    isDragging,
    ref,
    handleRef
  } = useSortable({
    id: props.metric.id,
    index: props.index,
    type: SORTABLE_METRIC_TYPE,
    group: props.type
  });
  const displayOptions = useMemo(() => props.metric.definition ? getFinalDisplayOptions(props.metric.definition, eventPropertyDefinitionsStore, metrics) : {}, [props.metric.definition, metrics]);
  const isMultiVariant = props.variantNames.length > 1;
  const rowStyle = useMemo(() => {
    var _props$results;
    if (isMultiVariant || props.results instanceof Error) {
      return undefined;
    }
    const firstVariant = (_props$results = props.results) === null || _props$results === void 0 ? void 0 : _props$results[props.variantNames[0]];
    if (!firstVariant || !firstVariant.statisticallySignificant) {
      return undefined;
    }
    const {
      statisticallySignificant
    } = firstVariant;
    if (statisticallySignificant === StatisticallySignificant.YES_POSITIVE) {
      return styles.rowPositive;
    }
    if (statisticallySignificant === StatisticallySignificant.YES_NEGATIVE) {
      return styles.rowNegative;
    }
    return undefined;
  }, [props.results, props.variantNames, isMultiVariant]);
  return <div className={clsx(styles.row, rowStyle)} ref={ref} data-dragging={isDragging} data-active={isActive} data-sentry-component="ExperimentPageSummaryMetricsCategoryRow" data-sentry-source-file="_row.tsx">
      <GrabHandle ref={handleRef} data-sentry-element="GrabHandle" data-sentry-source-file="_row.tsx" />
      <MetricName metric={props.metric} isMultiVariant={isMultiVariant} data-sentry-element="MetricName" data-sentry-source-file="_row.tsx" />
      <EditMetricButton metric={props.metric} data-sentry-element="EditMetricButton" data-sentry-source-file="_row.tsx" />
      <ExploreButton metric={props.metric} data-sentry-element="ExploreButton" data-sentry-source-file="_row.tsx" />
      <DeleteMetricButton id={props.metric.id} data-sentry-element="DeleteMetricButton" data-sentry-source-file="_row.tsx" />
      <div className={styles.variantsColumn}>
        {props.variantNames.map(variantName => {
        var _props$results2, _props$metric;
        if (props.results instanceof Error) {
          return <VariantRowError key={variantName} error={props.results} />;
        }
        const variantResults = (_props$results2 = props.results) === null || _props$results2 === void 0 ? void 0 : _props$results2[variantName];
        let className: string | undefined = undefined;
        switch (variantResults === null || variantResults === void 0 ? void 0 : variantResults.statisticallySignificant) {
          case StatisticallySignificant.YES_POSITIVE:
            className = styles.rowPositive;
            break;
          case StatisticallySignificant.YES_NEGATIVE:
            className = styles.rowNegative;
            break;
        }
        return <VariantRow key={variantName} className={clsx(className, {
          [styles.multiVariant]: isMultiVariant
        })} variantName={variantName} results={variantResults} isLoading={((_props$metric = props.metric) === null || _props$metric === void 0 ? void 0 : _props$metric.id) !== EMPTY_METRIC_ID && !props.results} displayOptions={displayOptions} metric={props.metric} absMax={props.absMax} displayVariantName={isMultiVariant} />;
      })}
      </div>
    </div>;
}